import { useMutation, useQuery } from '@tanstack/react-query';
import { api, type QueryParams } from '@/lib/api';

/**
 * Hooks für den Audit-Log-Viewer (Sprint 5, WT-508).
 *
 * Backend-Endpunkte:
 *   GET  /audit-log?filter[actor]=&filter[action]=&since=&limit=
 *                                                 -> AuditListResponse
 *   GET  /audit-log/verify                        -> AuditVerifyResponse
 *
 * Audit ist append-only (DB-Constraint). Hash-Chain-Verify rechnet die Kette
 * vom Genesis bis zum letzten Eintrag und meldet, ob ein Tampering stattfand.
 */

export interface AuditEntry {
  id: number;
  occurred_at: string;
  actor: string;
  action: string;
  entity_type: string;
  entity_id?: string | null;
  customer_id?: string | null;
  summary?: string | null;
  diff?: Record<string, unknown> | null;
  request_id?: string | null;
  prev_hash?: string | null;
  entry_hash?: string | null;
}

export interface AuditListResponse {
  data: AuditEntry[];
  total: number;
}

export const auditKeys = {
  all: ['audit'] as const,
  list: (filter: AuditFilter) => ['audit', 'list', filter] as const,
};

export interface AuditFilter {
  actor?: string;
  action?: string;
  entityType?: string;
  since?: string;
  limit?: number;
}

function auditFilterToParams(f: AuditFilter): QueryParams {
  const out: QueryParams = { limit: f.limit ?? 100 };
  if (f.actor) out['filter[actor]'] = f.actor;
  if (f.action) out['filter[action]'] = f.action;
  if (f.since) out.since = f.since;
  return out;
}

export function useAuditLog(filter: AuditFilter = {}): ReturnType<
  typeof useQuery<AuditListResponse>
> {
  return useQuery<AuditListResponse>({
    queryKey: auditKeys.list(filter),
    queryFn: () => api.get<AuditListResponse>('/audit-log', auditFilterToParams(filter)),
    staleTime: 30_000,
  });
}

export interface AuditVerifyResult {
  valid: boolean;
  entries_checked: number;
  broken_at?: number | null;
  broken_reason?: string | null;
}

export function useAuditVerify(): ReturnType<
  typeof useMutation<AuditVerifyResult, Error, void>
> {
  return useMutation<AuditVerifyResult, Error, void>({
    mutationFn: () => api.get<AuditVerifyResult>('/audit-log/verify'),
  });
}
